import { type CountryCode } from '@constants'
import { type CrossBrandMallHomeCampaign } from '@hooks-api/types/dataModel/cross-brand-mall'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Coupon2Filled } from '@pets-ui/Icons'
import { Image } from '@pets-ui/Image/Image'
import { Paper } from '@pets-ui/Paper/Paper'
import { Text } from '@pets-ui/Text/Text'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useMemo, useState } from 'react'
import highlightCouponBg from '../../assets/highlight-coupon-bg.png'
import { useHomeAuthorizedCampaigns } from '../hooks/use-home-authorized-campaigns'
import { BrandPromoContent, BrandPromoContentSkeleton } from './BrandPromoContent'
import { BrandTabList } from './BrandTabList'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  contentRow: {
    flexDirection: { [MOBILE]: 'column', default: 'row' },
    flexWrap: 'nowrap',
  },
  tabSidebar: {
    borderColor: colors.grey200,
    borderStyle: 'solid',
    flexShrink: 0,
    borderBottomWidth: { [MOBILE]: 1, default: 0 },
    borderRightWidth: { [MOBILE]: 0, default: 1 },
    width: { [MOBILE]: '100%', default: 120 },
  },
})

interface BrandPromoSectionProps {
  campaigns: CrossBrandMallHomeCampaign[]
  countryCode: CountryCode
  currency: string
  isDetailLoading: boolean
  basePath: string
}

export const BrandPromoSection = (props: BrandPromoSectionProps) => {
  const { campaigns, countryCode, currency, isDetailLoading, basePath } = props

  const authorizedCampaigns = useHomeAuthorizedCampaigns(campaigns)

  const eligibleCampaigns = useMemo(() => authorizedCampaigns
    .filter(campaign => campaign.highlight_coupons.length > 0)
    .sort((a, b) => a.display_order - b.display_order),
  [authorizedCampaigns])

  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(() => eligibleCampaigns[0]?.campaign_id ?? null)

  const activeCampaign = useMemo(() =>
    eligibleCampaigns.find(campaign => campaign.campaign_id === selectedCampaignId) ?? eligibleCampaigns[0] ?? null,
  [eligibleCampaigns, selectedCampaignId])

  if (!isDetailLoading && eligibleCampaigns.length === 0) {
    return null
  }

  return (
    <Paper elevation={0} overflow="hidden">
      <VStack>
        <Box position="relative" overflow="hidden" p={4} backgroundImage="linear-gradient(90deg, #FF8E9E 0%, #FFC9D1 100%)">
          <HStack gap={2} alignItems="center">
            <Coupon2Filled fontSize={20} color={colors.white} />
            <Text variant="h5" bold color={colors.white}>品牌優惠</Text>
            <Text color={colors.white}>上百優惠任你挑！</Text>
          </HStack>
          <Box position="absolute" right={0} top={0} height="100%">
            <Image src={highlightCouponBg} height="100%" objectFit="contain" />
          </Box>
        </Box>
        <HStack rootStyle={styles.contentRow}>
          <Box rootStyle={styles.tabSidebar}>
            <BrandTabList
              campaigns={eligibleCampaigns}
              activeCampaignId={activeCampaign?.campaign_id ?? null}
              onChange={setSelectedCampaignId}
              isLoading={isDetailLoading}
            />
          </Box>
          {isDetailLoading
            ? <BrandPromoContentSkeleton />
            : activeCampaign && (
              <BrandPromoContent
                key={activeCampaign.campaign_id}
                campaign={activeCampaign}
                countryCode={countryCode}
                currency={currency}
                basePath={basePath}
              />
            )}
        </HStack>
      </VStack>
    </Paper>
  )
}
